<template>
  <c-dropdown
    title="Select a date"
    :style="{ flexGrow: 1 }"
  >
    <c-focusable
      :show-border="showBorder"
    >
      <div :style="{ display: 'flex' }">
        <c-tag
          v-if="formattedResult === undefined"
          title="Empty"
          :style="{ margin: '0px 0 0px -6px', }"
          color="empty"
        />
        <c-tag
          v-else
          :style="{ margin: '4px 0 0px -6px', }"
          :title="formattedResult"
          color="plain"
        />
        <div
          v-if="formattedResult !== undefined"
          :style="{ marginLeft: 'auto' }"
        >
          <c-button
            icon="close-circle"
            fab
            size="xs"
            color="light"
            @click.stop="reset"
          />
        </div>
      </div>
    </c-focusable>
    <template #content>
      <c-card
        :style="cardStyle"
        contains-list
      >
        <div :style="inputWrapperStyle">
          <div>
            <c-input
              :value="startDate"
              size="small"
              :force-show-outline="selectedDate === 'start'"
              @click="selectedDate = 'start'"
              @change="parseDate($event, 'start')"
              @keydown.enter="parseDate($event.target.value, 'start')"
            />
          </div>
          <div>
            <c-input
              v-if="includeEndDate"
              show-background
              show-border
              :value="endDate"
              size="small"
              :force-show-outline="selectedDate === 'end'"
              @click="selectedDate = 'end'"
              @change="parseDate($event, 'end')"
              @keydown.enter="parseDate($event.target.value, 'end')"
            />
          </div>
        </div>
        <div :style="{ padding: '1.4rem' }">
          <div>
            <c-flex-layout :style="{ marginBottom: '1rem' }">
              <template #left>
                {{ monthFullName }} {{ year }}
              </template>
              <template #right>
                <div :style="{ display: 'flex', justifyContent: 'center' }">
                  <c-button
                    icon="left"
                    fab
                    size="small"
                    color="light"
                    @click="goToPreviousMonth"
                  />
                  <c-button
                    icon="right"
                    fab
                    size="small"
                    color="light"
                    @click="goToNextMonth"
                  />
                </div>
              </template>
            </c-flex-layout>
          </div>
          <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'center' }">
            <div
              v-for="(day, index) in weekdays"
              :key="index"
              :style="{ color: $colors.grey.lighten1, width: '3.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }"
            >
              {{ day }}
            </div>
          </div>
          <div>
            <div
              v-for="(week, j) in weeks"
              :key="j"
              :style="{ display: 'flex', justifyContent: 'center', alignItems: 'center' }"
            >
              <c-button
                v-for="(day, k) in week"
                :key="k"
                :color="getButtonColor(day)"
                hide-border
                fab
                size="small"
                :style="getButtonStyle(day)"
                @click="select(day)"
              >
                {{ day.date }}
              </c-button>
            </div>
          </div>
        </div>
        <template
          v-if="showIncludeEndDateButton"
        >
          <c-divider size="small" />
          <c-card-item
            title="End date"
            @click="includeEndDate = !includeEndDate"
          >
            <template #right>
              <c-switch :value="includeEndDate" />
            </template>
          </c-card-item>
        </template>
        <!--        <c-card-item-->
        <!--            title="Include time"-->
        <!--            @click="includeTime = !includeTime"-->
        <!--        >-->
        <!--          <template #right>-->
        <!--            <c-switch v-model:value="includeTime"/>-->
        <!--          </template>-->
        <!--        </c-card-item>-->
      </c-card>
    </template>
  </c-dropdown>
</template>

<script>
import CButton from 'shared/components/CButton.vue';
import CCard from 'shared/components/CCard.vue';
import CCardItem from 'shared/components/CCardItem.vue';
import CDivider from 'shared/components/CDivider.vue';
import CDropdown from 'shared/components/CDropdown.vue';
import CFlexLayout from 'shared/components/CFlexLayout.vue';
import CFocusable from 'shared/components/CFocusable.vue';
import CInput from 'shared/components/CInput.vue';
import CSwitch from 'shared/components/CSwitch.vue';
import CTag from 'shared/components/CTag.vue';
import equalObjects from 'lodash-es/_equalObjects';

export default {
  name: 'CDatePicker',
  props: {
    value: {
      type: Object,
      required: true,
    },
    predefined: {
      type: Boolean,
      default: true,
    },
    initiallyIncludeEndDate: {
      type: Boolean,
      default: false,
    },
    showBorder: {
      type: Boolean,
      default: false,
    },
    showIncludeEndDateButton: {
      type: Boolean,
      default: false,
    },
    initiallyIncludeTime: {
      type: Boolean,
      default: false,
    },
    showIncludeTimeButton: {
      type: Boolean,
      default: false,
    },
  },
  components: { CTag, CFocusable, CFlexLayout, CInput, CDropdown, CButton, CCard, CCardItem, CSwitch, CDivider },
  data() {
    return {
      start: null,
      end: null,
      formatEmpty: false,
      selectedDate: 'start',
      includeEndDate: false,
      includeTime: false,
      errorMessage: '',
      cardStyle: { minWidth: '25rem' },
      month: 1,
      year: 2022,
      weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      months: {
        0: 'January',
        1: 'February',
        2: 'March',
        3: 'April',
        4: 'May',
        5: 'June',
        6: 'July',
        7: 'August',
        8: 'September',
        9: 'October',
        10: 'November',
        11: 'December',
      },
      monthsShort: {
        0: 'Jan',
        1: 'Feb',
        2: 'Mar',
        3: 'Apr',
        4: 'May',
        5: 'Jun',
        6: 'Jul',
        7: 'Aug',
        8: 'Sep',
        9: 'Oct',
        10: 'Nov',
        11: 'Dec',
      },
      monthsShortRev: {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      },
    };
  },
  emits: ['update:value'],
  computed: {
    formattedResult() {
      if (this.formatEmpty) {
        return undefined;
      }
      if (!this.includeEndDate) {
        return this.startDate;
      }
      return `${this.startDate} - ${this.endDate}`;
    },
    inputWrapperStyle() {
      if (this.includeEndDate) {
        return { padding: '1.4rem 1.4rem 0', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem' };
      }
      return { padding: '1.4rem 1.4rem 0' };
    },
    startDate() {
      return this.formatDate(this.start);
    },
    endDate() {
      return this.formatDate(this.end);
    },
    monthFullName() {
      return this.months[this.month];
    },
    weeks() {
      const start = new Date(this.year, this.month, 1);
      const monthStartWeekDay = start.getDay();
      const lastDayOfPreviousMonth = new Date(this.year, this.month, 0).getDate();
      const lastMonthStart = lastDayOfPreviousMonth - (monthStartWeekDay - 1);
      const lastDayOfMonth = new Date(this.year, this.month + 1, 0);
      const lastDateOfMonth = lastDayOfMonth.getDate();
      const res = [];
      for (let i = 1; i <= 6; i++) {
        const w = [];
        for (let d = 1; d <= 7; d++) {
          const currentMonthDate = ((i - 1) * 7) + d - monthStartWeekDay;
          // first week
          if (currentMonthDate <= 0) {
            w.push({
              date: lastMonthStart + d - 1,
              month: this.getPreviousMonth(this.month),
              year: this.getYearOfPreviousMonth(this.month),
            });
            continue;
          }

          // last week
          if (currentMonthDate > lastDateOfMonth) {
            w.push({
              date: currentMonthDate - lastDateOfMonth,
              month: this.getNextMonth(this.month),
              year: this.getYearOfNextMonth(this.month),
            });
            continue;
          }

          w.push({ date: currentMonthDate, month: this.month, year: this.year });
        }

        res.push(w);
      }
      return res;
    },
  },
  methods: {
    parseDate(value, selectedDate) {
      try {
        const month = value.slice(0, 3);
        const date = parseInt(value.split(',')[0].slice(4), 10);
        const year = parseInt(value.split(',')[1], 10);
        if (this.monthsShortRev[month] === undefined) {
          throw new Error('month invalid');
        }
        if (date <= 0 || date > 31) {
          throw new Error('date invalid');
        }
        if (year <= 1974 || year > 2100) {
          throw new Error('year invalid');
        }
        const d = { date, month: this.monthsShortRev[month], year };
        if (this.equalDate(d, this[selectedDate])) {
          return;
        }
        this.select(d);
      } catch (e) {
        this.errorMessage = 'invalid date';
      }
    },
    equalDate(date1, date2) {
      if (date1 === null || date2 === null) {
        return false;
      }
      return date1.year === date2.year && date1.month === date2.month && date1.date === date2.date;
    },
    isAfter(date1, date2) {
      if (date1 === null || date2 === null) {
        return false;
      }
      if (date1.year > date2.year) {
        return true;
      }
      if (date1.year < date2.year) {
        return false;
      }
      if (date1.month > date2.month) {
        return true;
      }
      if (date1.month < date2.month) {
        return false;
      }
      return date1.date > date2.date;
    },
    isBefore(date1, date2) {
      if (date1 === null || date2 === null) {
        return false;
      }
      if (date1.year < date2.year) {
        return true;
      }
      if (date1.year > date2.year) {
        return false;
      }
      if (date1.month < date2.month) {
        return true;
      }
      if (date1.month > date2.month) {
        return false;
      }
      return date1.date < date2.date;
    },
    getButtonColor(day) {
      return day.month === this.month ? 'default' : 'superlight';
    },
    getButtonStyle(day) {
      const equalsStart = this.equalDate(this.start, day);
      const equalsEnd = this.equalDate(this.end, day);
      const defaultStyle = { width: '3.2rem', height: '3.2rem' };
      if (equalsEnd && this.selectedDate === 'end') {
        if (this.start !== null && !this.equalDate(this.start, this.end)) {
          return {
            ...defaultStyle,
            backgroundColor: this.$colors.blue.base,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            color: 'white',
          };
        }
        return { ...defaultStyle, backgroundColor: this.$colors.blue.base, color: 'white' };
      }
      if (equalsStart && this.selectedDate === 'start') {
        if (this.includeEndDate && this.end !== null && !this.equalDate(this.start, this.end)) {
          return {
            ...defaultStyle,
            backgroundColor: this.$colors.blue.base,
            color: 'white',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          };
        }
        return { ...defaultStyle, backgroundColor: this.$colors.blue.base, color: 'white' };
      }
      if (this.includeEndDate && equalsEnd) {
        return {
          ...defaultStyle,
          backgroundColor: this.$colors.blue.lighten2,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        };
      }
      if (equalsStart) {
        return {
          ...defaultStyle,
          backgroundColor: this.$colors.blue.lighten2,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        };
      }
      if (this.includeEndDate && this.isAfter(day, this.start) && this.isBefore(day, this.end)) {
        return { ...defaultStyle, backgroundColor: this.$colors.blue.lighten3, borderRadius: 0 };
      }
      return defaultStyle;
    },
    formatDate(date) {
      if (date === null) {
        return '';
      }

      return `${this.monthsShort[date.month]} ${date.date}, ${date.year}`;
    },
    select(day) {
      this[this.selectedDate] = day;
      this.month = day.month;
      this.year = day.year;
      if (this.includeEndDate && this.selectedDate === 'start') {
        this.selectedDate = 'end';
      }
    },
    reset() {
      this.start = null;
      this.end = null;
      this.formatEmpty = true;
    },
    getYearOfNextMonth(month) {
      if (month === 11) {
        return this.year + 1;
      }
      return this.year;
    },
    getYearOfPreviousMonth(month) {
      if (month === 0) {
        return this.year - 1;
      }
      return this.year;
    },
    getNextMonth() {
      if (this.month === 11) {
        return 0;
      }
      return this.month + 1;
    },
    getPreviousMonth() {
      if (this.month === 0) {
        return 11;
      }
      return this.month - 1;
    },
    goToNextMonth() {
      if (this.month === 11) {
        this.year += 1;
        this.month = 0;
      }

      this.month += 1;
    },
    goToPreviousMonth() {
      if (this.month === 0) {
        this.year -= 1;
        this.month = 11;
      }

      this.month -= 1;
    },
    toISOString(date) {
      if (date === null) {
        return null;
      }
      return `${date.year}-${this.zeroPad(date.month + 1, 2)}-${this.zeroPad(date.date, 2)}`;
    },
    zeroPad(num, places) {
      return String(num).padStart(places, '0');
    },
    getValue() {
      if (this.includeEndDate) {
        return {
          start: this.toISOString(this.start),
          end: this.toISOString(this.end),
        };
      }
      return { start: this.toISOString(this.start) };
    },
  },
  watch: {
    includeEndDate(val) {
      if (!val) {
        this.selectedDate = 'start';
      }
    },
    start(newVal, oldVal) {
      if (equalObjects(oldVal, newVal)) {
        return;
      }
      if (newVal !== null) {
        this.formatEmpty = false;
      }
      this.$emit('update:value', this.getValue());
    },
    end(newVal, oldVal) {
      if (equalObjects(oldVal, newVal)) {
        return;
      }
      if (newVal !== null) {
        this.formatEmpty = false;
      }
      this.$emit('update:value', this.getValue());
    },
  },
  created() {
    this.formatEmpty = this.value.start === null || this.value.start === undefined;

    const now = new Date();
    ['start', 'end'].forEach((key) => {
      if (this.value[key] !== undefined && this.value[key] !== null) {
        try {
          const d = new Date(`${this.value[key]}T00:00`); // compensates time zones!
          this[key] = {
            date: d.getDate(),
            month: d.getMonth(),
            year: d.getFullYear(),
          };
        } catch {
          // Do nothing
        }
      }
    });
    if (this.start !== null) {
      this.month = this.start.month;
      this.year = this.start.year;
      return;
    }

    this.month = now.getMonth();
    this.year = now.getFullYear();
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>

</style>
